import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Megaphone, Send, Eye, Loader2, History, Users, Flame, CalendarCheck, Newspaper, Mail, ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/dashboard/PageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'announcement', label: 'Announcement', icon: Megaphone, active: 'bg-[#2447D6] text-white' },
  { key: 'motivation', label: 'Motivation', icon: Flame, active: 'bg-orange-500 text-white' },
  { key: 'recap', label: 'Weekly Recap', icon: CalendarCheck, active: 'bg-emerald-600 text-white' },
  { key: 'newsletter', label: 'Newsletter', icon: Newspaper, active: 'bg-violet-600 text-white' },
];

const SENDERS = [
  { key: 'kyle', label: 'Kyle Byrd — CGO & Co-Owner' },
  { key: 'brandon', label: 'Brandon Beal — Founder / CEO' },
  { key: 'generic', label: 'Breeze Financial Group (generic)' },
];

const CAT_BADGE = {
  announcement: 'bg-blue-100 text-blue-700',
  motivation: 'bg-orange-100 text-orange-700',
  recap: 'bg-emerald-100 text-emerald-700',
  newsletter: 'bg-violet-100 text-violet-700',
};

const inputCls = 'w-full text-sm px-3 py-2 rounded-lg border border-[#E9EDF7] bg-white text-[#2B3674] placeholder:text-[#A3AED0] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40';

export default function EmailBlast() {
  const { user, getAuthHeader } = useAuth();
  const [category, setCategory] = useState('announcement');
  const [sender, setSender] = useState('kyle');
  const [subject, setSubject] = useState('');
  const [headline, setHeadline] = useState('');
  const [body, setBody] = useState('');
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [history, setHistory] = useState(null);

  const fetchHistory = useCallback(() => {
    axios.get(`${API}/admin/email-blast/history`, getAuthHeader())
      .then((res) => setHistory(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHistory([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  if (user && user.role !== 'admin') return <Navigate to="/" replace />;

  const paraCount = body.split(/\n\s*\n/).filter((t) => t.trim()).length;
  const positionOptions = [
    { value: 'banner', label: 'Top banner (above headline)' },
    ...Array.from({ length: paraCount }, (_, i) => ({ value: `p${i + 1}`, label: `After paragraph ${i + 1}` })),
    { value: 'end', label: 'Bottom (before signature)' },
  ];

  const addImages = (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
    if (!list.length) return;
    if (images.length + list.length > 4) { toast.error('Max 4 images per blast'); return; }
    list.forEach((f) => {
      if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} is over 4MB`); return; }
      const reader = new FileReader();
      reader.onload = () => {
        setImages((prev) => [...prev, { id: `${Date.now()}-${f.name}`, filename: f.name, data: reader.result, position: 'end' }]);
      };
      reader.readAsDataURL(f);
    });
  };

  const payload = {
    subject, headline, body, category, sender,
    images: images.map(({ data, position, filename }) => ({ data, position, filename })),
  };

  const openPreview = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message are required'); return; }
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/admin/email-blast/preview`, payload, getAuthHeader());
      setPreview(res.data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTest = async () => {
    setTestSending(true);
    try {
      const res = await axios.post(`${API}/admin/email-blast/test`, payload, getAuthHeader());
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setTestSending(false);
    }
  };

  const sendBlast = async () => {
    if (!window.confirm(`Send this blast to all ${preview?.recipient_count} team members? This cannot be undone.`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/email-blast/send`, payload, getAuthHeader());
      toast.success(res.data.message);
      setPreviewOpen(false);
      setSubject(''); setHeadline(''); setBody(''); setImages([]);
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Blast failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-6" data-testid="email-blast-page">
      <PageHeader
        title="Email Blast"
        subtitle="Send a Breeze-branded email to the entire company — announcements, motivation, recaps, newsletters."
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Compose */}
        <Card className="bg-white lg:col-span-3">
          <CardContent className="p-5 space-y-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#A3AED0]">Compose</div>

            <div>
              <label className="block text-xs font-semibold text-[#2B3674] mb-1.5">Message Type</label>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    data-testid={`blast-category-${c.key}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      category === c.key ? c.active : 'bg-white border border-[#E9EDF7] text-[#2B3674]/80 hover:bg-slate-50'
                    }`}
                  >
                    <c.icon className="h-3.5 w-3.5" /> {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#2B3674] mb-1">Send As</label>
              <Select value={sender} onValueChange={setSender}>
                <SelectTrigger className="text-sm" data-testid="blast-sender-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SENDERS.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#2B3674] mb-1">Subject Line *</label>
              <input className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="Big news for the Breeze team..." data-testid="blast-subject" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#2B3674] mb-1">Headline <span className="text-[#A3AED0] font-normal">(shown big at the top — optional)</span></label>
              <input className={inputCls} value={headline} onChange={(e) => setHeadline(e.target.value)}
                placeholder="We just crossed $1M in issued AP 🎉" data-testid="blast-headline" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#2B3674] mb-1">Message *</label>
              <textarea className={`${inputCls} min-h-[220px]`} value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={'Write your message here.\n\nSeparate paragraphs with a blank line — they will be formatted automatically in the branded email.'}
                data-testid="blast-body" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#2B3674] mb-1">
                Images <span className="text-[#A3AED0] font-normal">(optional — place them exactly where you want, up to 4)</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { addImages(e.target.files); e.target.value = ''; }}
                data-testid="blast-image-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="blast-add-image-btn"
                className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl border-2 border-dashed border-[#D9E1F2] text-sm font-semibold text-[#2447D6] hover:bg-blue-50/50 transition-colors"
              >
                <ImagePlus className="h-4 w-4" /> Add Image{images.length > 0 ? ` (${images.length}/4)` : 's'}
              </button>
              {images.length > 0 && (
                <div className="mt-2 space-y-2">
                  {images.map((img) => (
                    <div key={img.id} className="flex items-center gap-3 rounded-xl border border-[#E9EDF7] bg-[#F8FAFF] p-2" data-testid={`blast-image-row-${img.id}`}>
                      <img src={img.data} alt={img.filename} className="h-12 w-12 rounded-lg object-cover flex-shrink-0 border border-[#E9EDF7]" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-[#2B3674] truncate">{img.filename}</div>
                        <select
                          value={img.position}
                          onChange={(e) => setImages((prev) => prev.map((x) => (x.id === img.id ? { ...x, position: e.target.value } : x)))}
                          className="mt-1 w-full text-xs px-2 py-1.5 rounded-lg border border-[#E9EDF7] bg-white text-[#2B3674] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40"
                          data-testid={`blast-image-position-${img.id}`}
                        >
                          {positionOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => setImages((prev) => prev.filter((x) => x.id !== img.id))}
                        className="p-1.5 rounded-lg text-[#A3AED0] hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                        data-testid={`blast-image-remove-${img.id}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              onClick={openPreview}
              disabled={previewLoading}
              data-testid="blast-preview-btn"
              className="w-full bg-[#2447D6] hover:bg-[#1E3FBF] text-white rounded-xl h-11"
            >
              {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Eye className="h-4 w-4 mr-1.5" /> Preview Branded Email</>)}
            </Button>
          </CardContent>
        </Card>

        {/* History */}
        <Card className="bg-white lg:col-span-2">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#A3AED0]">
              <History className="h-3.5 w-3.5" /> Past Blasts
            </div>
            {history === null ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#2447D6]" /></div>
            ) : history.length === 0 ? (
              <p className="text-sm text-[#A3AED0] py-6 text-center">No blasts sent yet. Your history will show up here.</p>
            ) : (
              <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                {history.map((h) => (
                  <div key={h.id} className="rounded-xl border border-[#E9EDF7] bg-[#F8FAFF] p-3" data-testid={`blast-history-${h.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${CAT_BADGE[h.category] || CAT_BADGE.announcement}`}>
                        {CATEGORIES.find((c) => c.key === h.category)?.label || h.category}
                      </span>
                      <span className="text-[10px] text-[#A3AED0]">
                        {h.sent_at ? new Date(h.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-[#2B3674] mt-1 truncate">{h.subject}</div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-[#A3AED0]">
                      <span>From: {h.from_name}</span>
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {h.sent_count}/{h.recipient_count} delivered</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preview / send dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="blast-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-[#2447D6]" /> Preview — Company Blast
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="text-xs text-[#2B3674] bg-[#F4F7FE] border border-[#E9EDF7] rounded-lg px-3 py-2 space-y-0.5">
                <div><span className="text-[#A3AED0]">To:</span> <span className="font-semibold" data-testid="blast-recipient-count">Entire company — {preview.recipient_count} active team members</span></div>
                <div><span className="text-[#A3AED0]">From:</span> <span className="font-semibold">{preview.from_name}</span></div>
                <div><span className="text-[#A3AED0]">Subject:</span> <span className="font-semibold">{preview.subject}</span></div>
                {preview.reply_to && <div><span className="text-[#A3AED0]">Replies go to:</span> <span className="font-semibold">{preview.reply_to}</span></div>}
              </div>
              <div className="border border-[#E9EDF7] rounded-xl overflow-hidden bg-[#EAF3FB]">
                <iframe title="Blast preview" srcDoc={preview.html} className="w-full h-[52vh] bg-white" sandbox="" data-testid="blast-preview-frame" />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setPreviewOpen(false)}>Keep Editing</Button>
                <Button
                  variant="outline"
                  onClick={sendTest}
                  disabled={testSending || sending}
                  data-testid="blast-test-btn"
                  className="flex-1 rounded-xl border-[#2447D6]/40 text-[#2447D6] hover:bg-blue-50"
                >
                  {testSending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Mail className="h-4 w-4 mr-1.5" /> Send Test to Myself</>)}
                </Button>
                <Button
                  onClick={sendBlast}
                  disabled={sending || testSending}
                  data-testid="blast-send-btn"
                  className="flex-1 bg-[#2447D6] hover:bg-[#1E3FBF] text-white rounded-xl"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="h-4 w-4 mr-1.5" /> Send to {preview.recipient_count} People</>)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
